import type { Option, SelectProps } from "@gadgetinc/widgets";
import { Select, SelectUtils } from "@gadgetinc/widgets";
import { useStyletron } from "baseui";
import { castArray, isUndefined, omit } from "lodash";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { assert } from "../../lib/utils";
import type { BindingProps } from "./useBinding";
import { useBinding } from "./useBinding";
import type { NodeWithProblems } from "./useProblemMessages";
import { useBoundNodeHasProblems } from "./useProblemMessages";
import type { DisabledOnProdProps } from "./useDisableProd";
export type { Option } from "@gadgetinc/widgets";

export const useSelectedValue = (options: Option[], value: string | string[]) => {
  // Baseweb's select's value prop takes an array of Option objects, even when it's not a multiselect. So, pull out an array of Options from the options to pass as the value based on the value stored on the node. The value on the node is assumed to be an array if props.multi and a string if not.
  const ids: (string | number | undefined)[] = castArray(value);
  return options.filter((option) => ids.includes(option.id));
};

export type BoundSelectProps<Node extends NodeWithProblems, Value extends string | string[] = string> = SelectProps &
  BindingProps<Node, Value> &
  DisabledOnProdProps<Node>;

const BoundSelectInner = React.forwardRef(
  <Node extends NodeWithProblems, Value extends string | string[]>(
    props: BoundSelectProps<Node, Value> & { dataTestId?: string },
    ref: React.Ref<any>
  ) => {
    const [value, setValue] = useBinding(props);
    const { error } = useBoundNodeHasProblems(props);
    const [_css, $theme] = useStyletron();

    const normalizedOptions = useMemo(
      () => SelectUtils.normalizeOptions(assert(props.options, "no options passed to BoundSelect")),
      [props.options]
    );
    const selectValues = useSelectedValue(normalizedOptions, value);

    return (
      <Select
        overrides={{
          OptionContent: {
            style: {
              color: $theme.colors.primaryA,
            },
          },
        }}
        backspaceRemoves={props.clearable ?? false}
        deleteRemoves={props.clearable ?? false}
        controlRef={ref}
        escapeClearsValue={false}
        searchable={props.searchable}
        error={error}
        {...omit(props, ["property", "getter", "setter"])}
        value={selectValues}
        onChange={(params) => {
          const newID: Value = (props.multi ? params.value.map((option) => option.id) : params.value?.[0]?.id) as any;

          // currently the select field acts like a text field in that you can delete the value that's set
          // instead of just deleting the characters you searched for. this adds some protection to prevent
          // the field from breaking by also considering the required prop
          const isSettable = (isUndefined(newID) && !props.required) || !isUndefined(newID);
          if (isSettable) setValue(newID);
        }}
      />
    );
  }
);
export const BoundSelect = observer(BoundSelectInner);
